import {checkCancellation, WithCancellation} from "../../tasks";
import {fitBitmapToBytes} from "./image-resizing.util";
import {withExtension} from "./file-name.util";

/**
 * Every mime `file-type` reports for the HEIF family.
 *
 * All four matter. iOS most often writes the `mif1` brand, which sniffs as `image/heif` — the
 * previous code only tested `image/heic`, so those photos were never converted *and* failed the
 * allow-list, surfacing to the user as "please select a file" right after they selected one.
 */
export const HeifMimeTypes = [
  'image/heic',
  'image/heic-sequence',
  'image/heif',
  'image/heif-sequence'
] as const;

export type HeifMimeType = typeof HeifMimeTypes[number];

export function isHeifMimeType(mime: string | undefined | null): mime is HeifMimeType {
  return !!mime && (HeifMimeTypes as readonly string[]).includes(mime);
}

/** Raised when neither the platform decoder nor the WASM fallback could read a HEIF image. */
export class HeifDecodeError extends Error {
  constructor(
    readonly mime: string | undefined,
    readonly nativeError: unknown,
    readonly wasmError?: unknown
  ) {
    super(`Failed to decode HEIF image${mime ? ` (${mime})` : ''}`);
    this.name = 'HeifDecodeError';
    // Keep the underlying failure on the cause chain so Sentry groups on something meaningful.
    this.cause = wasmError ?? nativeError;
  }
}

export type HeifBitmapDecoder = (blob: Blob, options?: ImageBitmapOptions) => Promise<ImageBitmap>;

/**
 * Indirection over the `heic-to` dynamic import.
 *
 * Two jobs: keep the ~720 KB gz decoder out of every user's bundle by holding the `import()`
 * inside a function (module-scope evaluation is what made the old decoder load for everyone the
 * moment the insurance step rendered), and give specs a seam so they can exercise the fallback
 * without pulling libheif into the Karma bundle.
 */
export const HeifWasmLoader: { load(): Promise<HeifBitmapDecoder> } = {
  async load(): Promise<HeifBitmapDecoder> {
    const {heicTo} = await import('heic-to');
    return (blob, options) => heicTo({blob, type: 'bitmap', options});
  }
};

/**
 * Decodes a HEIF blob to an `ImageBitmap`, preferring the platform decoder.
 *
 * Native first is both faster and more correct: WebKit runs the full ImageIO pipeline, applying
 * `irot`/`imir` and colour-managing the embedded ICC profile (Display P3 on every iPhone since the
 * XS), and it tracks whatever Apple's encoder emits by construction. libheif returns raw,
 * un-colour-managed planes and has to be upgraded every time the format moves.
 *
 * Capability is probed per file rather than cached or inferred from the user agent — "WebKit
 * decodes HEIC" is not the same claim as "WebKit decodes *this* HEIC" (10-bit HDR and gain-map
 * variants), and Chromium on Windows with the HEVC Video Extension installed does decode. The
 * native attempt fails in milliseconds, so there is nothing to optimise here.
 */
export async function decodeHeifBitmap(blob: Blob, opts?: WithCancellation<object>): Promise<ImageBitmap> {

  const cancellationToken = opts?.cancellationToken;
  checkCancellation(cancellationToken);

  let nativeError: unknown;
  try {
    const bitmap = await createImageBitmap(blob, {imageOrientation: 'from-image'});
    // Some engines resolve a degenerate bitmap instead of rejecting, which then draws nothing.
    if (bitmap.width > 0 && bitmap.height > 0) {
      return bitmap;
    }
    bitmap.close();
    nativeError = new Error('Native decoder produced a zero-dimension bitmap');
  } catch (e) {
    nativeError = e;
  }

  checkCancellation(cancellationToken);

  try {
    const decode = await HeifWasmLoader.load();
    checkCancellation(cancellationToken);
    return await decode(blob, {imageOrientation: 'from-image'});
  } catch (e) {
    // Cancellation is control flow, not a decode failure — let it through untouched.
    checkCancellation(cancellationToken);
    throw new HeifDecodeError(blob.type || undefined, nativeError, e);
  }
}

export interface HeifToJpegOptions {
  readonly quality?: number;
  readonly maxBytes?: number;
  readonly maxDurationMs?: number;
}

/**
 * Transcodes a HEIF file to JPEG, decoding once and encoding once.
 *
 * Note there is no orientation correction applied on top of the decode. libheif already applies
 * the container's `irot`/`imir`, and Apple HEICs carry *both* that and an EXIF Orientation tag —
 * honouring both double-rotates the image.
 */
export async function heifToJpeg(file: File, opts?: WithCancellation<HeifToJpegOptions>): Promise<File> {

  const cancellationToken = opts?.cancellationToken;
  const bitmap = await decodeHeifBitmap(file, {cancellationToken});
  try {
    checkCancellation(cancellationToken);
    const blob = await fitBitmapToBytes(bitmap, {
      // Without a cap, fall back to the bitmap's natural size — Number.MAX_SAFE_INTEGER makes the
      // first full-size encode always satisfy the search.
      maxBytes: opts?.maxBytes ?? Number.MAX_SAFE_INTEGER,
      type: 'image/jpeg',
      quality: opts?.quality,
      maxDurationMs: opts?.maxDurationMs,
      cancellationToken
    });
    return new File([blob], withExtension(file.name, 'jpg'), {type: 'image/jpeg'});
  } finally {
    bitmap.close();
  }
}
